import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../store/redux/store/store';
import {
  BusinessRoleView,
  BusinessRoleType,
  BusinessRoleStatus,
  EquipmentPriority,
  NeededEquipmentItem,
  CreateBusinessRoleInput,
  UpdateBusinessRoleInput,
  BusinessRoleDependencyInput,
  BusinessRoleSuccessCriteriaInput,
  RequiredSkillItemInput,
  SkillMappingSuggestionItemView,
  createBusinessRole,
  updateBusinessRole,
  getBusinessRoles,
  suggestRole,
  suggestSuccessCriteria,
  suggestRequiredSkills,
  suggestSkillMappings,
} from '../../../api/General/businessRoles';
import { AssetCategoryView, getCategories as getAssetCategories } from '../../../api/General/assets';
import { BusinessRoleCategory, addCategory as addRoleCategory, getCategories as getRoleCategories, getRoleReviewSettings } from '../../../api/General/businessRoleSettings';
import { Department } from '../../../models/general';
import { SkillView } from '../../../models/employee';
import { getDepartments } from '../../../api/General/Company';
import SkillAutocomplete from '../../UserProfile/Skills/components/SkillAutocomplete';
import {
  Dialog, DialogContent, DialogHeader, DialogBody, DialogFooter,
  DialogTitle,
} from '../../../components/ui/Dialog';
import Button from '../../../components/ui/Button';
import AiGeneratedBadge from '../../../components/ui/AiGeneratedBadge';
import ClaritySavingHint from '@/components/clarity/ClaritySavingHint';
import EquipmentCategoryPicker from '../../../components/assets/EquipmentCategoryPicker';
import CreatableSelect from '../../../components/ui/CreatableSelect';
import { useAiFilledFields } from '../../../hooks/useAiFilledFields';
import { createSkill } from '../../../api/Employee/skills';
import { addUpdateDepartments } from '../../../api/General/Company';
import { priorityToneClass, priorityOrder } from '@/components/assets/equipmentRequirementChrome';

// The fields autofill can write, so a marker can be shown and cleared per field.
type AiField =
  | 'description' | 'category' | 'coreResponsibilities' | 'decisionRights'
  | 'dependencies' | 'requiredSkills' | 'requiredSkillItems' | 'neededEquipment'
  | 'relatedEquipment' | 'successCriteria' | 'tags';

const roleTypeLabels: Record<BusinessRoleType, string> = {
  [BusinessRoleType.Department]: 'Department',
  [BusinessRoleType.Project]: 'Project',
  [BusinessRoleType.Temporary]: 'Temporary',
};

const statusLabels: Record<BusinessRoleStatus, string> = {
  [BusinessRoleStatus.Active]: 'Active',
  [BusinessRoleStatus.Inactive]: 'Inactive',
  [BusinessRoleStatus.Draft]: 'Draft',
};

interface Props {
  role: BusinessRoleView | null;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  // Server-derived. When false, the department is forced to scopeDepartmentId and the field is read-only.
  isCompanyScope?: boolean;
  scopeDepartmentId?: number;
  scopeDepartmentName?: string;
}


function toDateStr(d: Date): string {
  return d.toISOString().split('T')[0];
}

export default function RoleEditModal({
  role,
  open,
  onClose,
  onSaved,
  isCompanyScope = true,
  scopeDepartmentId,
  scopeDepartmentName,
}: Props) {
  const { t } = useTranslation();
  const isEdit = !!role;

  const companyId = useSelector((state: RootState) => state.user.company?.id as number | undefined);
  const currentUserName = useSelector((state: RootState) => state.user.fullName as string);

  const [name, setName] = useState('');
  const [type, setType] = useState<BusinessRoleType>(BusinessRoleType.Department);
  const [departmentId, setDepartmentId] = useState<number | undefined>(undefined);
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState<BusinessRoleStatus>(BusinessRoleStatus.Draft);
  const [numberOfSeats, setNumberOfSeats] = useState(1);
  const [coreResponsibilities, setCoreResponsibilities] = useState<string[]>([]);
  const [newResponsibility, setNewResponsibility] = useState('');
  const [decisionRights, setDecisionRights] = useState<string[]>([]);
  const [newDecisionRight, setNewDecisionRight] = useState('');
  const [dependencies, setDependencies] = useState<BusinessRoleDependencyInput[]>([]);
  const [successCriteria, setSuccessCriteria] = useState<BusinessRoleSuccessCriteriaInput>({});
  const [tags, setTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState('');
  const [requiredSkills, setRequiredSkills] = useState<string[]>([]);
  const [requiredSkillItems, setRequiredSkillItems] = useState<RequiredSkillItemInput[]>([]);
  // Name cache for skill ids, not a preloaded list: no endpoint returns the whole company
  // catalogue (SearchSkills needs a term; the inventory only covers skills someone already
  // holds). Filled from the role, AI suggestions and autocomplete picks so rows show a name.
  const [skillCatalogue, setSkillCatalogue] = useState<SkillView[]>([]);
  const [skillMappings, setSkillMappings] = useState<SkillMappingSuggestionItemView[]>([]);
  const [skillQuery, setSkillQuery] = useState('');
  const [neededEquipment, setNeededEquipment] = useState<NeededEquipmentItem[]>([]);
  const [relatedEquipment, setRelatedEquipment] = useState<number[]>([]);
  const [categories, setCategories] = useState<AssetCategoryView[]>([]);
  const [roleCategories, setRoleCategories] = useState<BusinessRoleCategory[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [roleNames, setRoleNames] = useState<string[]>([]);
  const [reviewIntervalMonths, setReviewIntervalMonths] = useState(6);
  const categoryNameById = new Map(categories.map((c) => [c.id, c.name]));
  const skillNameById = new Map(skillCatalogue.map((s) => [s.id, s.name]));
  const level2Categories = categories.filter((c) => c.level === 2);
  // The server may omit the scope name (archived department), so fall back to the loaded list.
  const lockedDepartmentName = scopeDepartmentName
    ?? departments.find((d) => d.id === scopeDepartmentId)?.name
    ?? '';
  // Lock only when the server actually handed us a department. Scope is unknown until stats
  // resolve, and forcing an empty one there would post no department and orphan the role.
  const departmentLocked = !isCompanyScope && scopeDepartmentId != null;
  const [revisionComment, setRevisionComment] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const errorRef = useRef<HTMLDivElement>(null);
  const { isAiFilled, markAiFilled, clearAiFilled, resetAiFilled } = useAiFilledFields<AiField>();
  const currentRole = useSelector((state: RootState) => state.user.role);
  // AddUpdateDepartments is Policy "Hr", which resolves to Hr or Admin; Manager and Chief reach this modal too.
  // AddUpdateDepartments and AddCategory are both Policy "Hr", which resolves to Hr or Admin.
  const canCreateLookups = currentRole === 'Hr' || currentRole === 'Admin';
  const canCreateDepartment = canCreateLookups;

  // AI assistance state (Q1 Role-Clarity).
  const [aiFilling, setAiFilling] = useState(false);
  const [aiCriteria, setAiCriteria] = useState(false);
  const [skillMapping, setSkillMapping] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [aiNotice, setAiNotice] = useState('');

  const today = new Date();
  const todayStr = toDateStr(today);
  const nextReviewStr = (() => {
    const d = new Date(today);
    d.setMonth(d.getMonth() + reviewIntervalMonths);
    return toDateStr(d);
  })();

  // Refetch on every open so lists reflect the CURRENT company (the modal stays mounted
  // and would otherwise go stale after an Operator company create/switch without a reload).
  useEffect(() => {
    if (!open) return;
    getAssetCategories().then((result) => {
      if (result.errors.length === 0) setCategories(result.payload ?? []);
    });
    getRoleCategories().then((result) => {
      if (result.errors.length === 0) setRoleCategories((result.payload ?? []).filter((c) => c.isActive));
    });
    getRoleReviewSettings().then((result) => {
      if (result.errors.length === 0 && result.payload) setReviewIntervalMonths(result.payload.reviewIntervalMonths);
    });
    getBusinessRoles({ pageSize: 200 }).then((result) => {
      // A dependency stores the role NAME, so two roles sharing one are indistinguishable here:
      // the duplicate offers no choice and collides on its React key. Keep the first.
      if (result.errors.length === 0) {
        setRoleNames([...new Set((result.payload?.items ?? []).map((r) => r.name))]);
      }
    });
    if (companyId) {
      getDepartments(companyId).then((result) => {
        if (result.errors.length === 0) setDepartments(result.payload ?? []);
      });
    }
  }, [open, companyId]);

  useEffect(() => {
    // Loading different values into the form makes any AI marker from the previous role a lie.
    resetAiFilled();
    if (open && role) {
      setName(role.name);
      setType(role.type);
      setDepartmentId(role.departmentId);
      setDescription(role.description || '');
      setCategory(role.category || '');
      setStatus(role.status);
      setNumberOfSeats(role.numberOfSeats);
      setCoreResponsibilities([...role.coreResponsibilities]);
      setDecisionRights([...role.decisionRights]);
      setDependencies(role.dependencies.map(d => ({ role: d.role, description: d.description })));
      setSuccessCriteria(role.successCriteria || {});
      setTags([...role.tags]);
      setRequiredSkills([...role.requiredSkills]);
      setRequiredSkillItems(role.requiredSkillItems?.map((i) => ({ skillId: i.skillId, importance: i.importance })) ?? []);
      setSkillCatalogue(
        (role.requiredSkillItems ?? [])
          .filter((i) => !!i.skillName)
          .map((i) => ({ id: i.skillId, name: i.skillName as string }))
      );
      setSkillMappings([]);
      setSkillQuery('');
      setNeededEquipment(Array.isArray(role.neededEquipment) ? role.neededEquipment : []);
      setRelatedEquipment(Array.isArray(role.relatedEquipment) ? role.relatedEquipment : []);
      setRevisionComment('');
    } else if (open && !role) {
      setName('');
      setType(BusinessRoleType.Department);
      setDepartmentId(undefined);
      setDescription('');
      setCategory('');
      setStatus(BusinessRoleStatus.Draft);
      setNumberOfSeats(1);
      setCoreResponsibilities([]);
      setDecisionRights([]);
      setDependencies([]);
      setSuccessCriteria({});
      setTags([]);
      setRequiredSkills([]);
      setRequiredSkillItems([]);
      setSkillCatalogue([]);
      setSkillMappings([]);
      setSkillQuery('');
      setNeededEquipment([]);
      setRelatedEquipment([]);
      setRevisionComment('');
    }
    setShowDisclaimer(false);
    setAiNotice('');
    setError('');
  }, [open, role, resetAiFilled]);

  // Scope can resolve after the modal is already open, so force the department without resetting the form.
  useEffect(() => {
    if (open && !role && departmentLocked) setDepartmentId(scopeDepartmentId);
  }, [open, role, departmentLocked, scopeDepartmentId]);

  const handleTypeChange = (next: BusinessRoleType) => {
    setType(next);
    // A scoped manager owns every type they create, so their department survives a type change.
    if (next !== BusinessRoleType.Department && !departmentLocked) setDepartmentId(undefined);
  };


  const handleSave = async () => {
    if (!name.trim()) {
      setError(t('businessRoles.errors.nameRequired'));
      return;
    }
    // Without this a department role can be saved with no department, orphaning it with no UI path back.
    if (type === BusinessRoleType.Department && !departmentId) {
      setError(t('businessRoles.errors.departmentRequired'));
      return;
    }

    setSaving(true);
    setError('');

    try {
      const base: CreateBusinessRoleInput = {
        name: name.trim(),
        type,
        departmentId: departmentLocked
          ? scopeDepartmentId
          : (type === BusinessRoleType.Department ? departmentId : undefined),
        description: description || undefined,
        category: category || undefined,
        status,
        numberOfSeats,
        coreResponsibilities: coreResponsibilities.length > 0 ? coreResponsibilities : undefined,
        decisionRights: decisionRights.length > 0 ? decisionRights : undefined,
        dependencies: dependencies.length > 0 ? dependencies : undefined,
        successCriteria: successCriteria.description ? successCriteria : undefined,
        tags: tags.length > 0 ? tags : undefined,
        requiredSkills: requiredSkills.length > 0 ? requiredSkills : undefined,
        requiredSkillItems: requiredSkillItems.length > 0 ? requiredSkillItems : undefined,
        neededEquipment: neededEquipment.length > 0 ? neededEquipment : undefined,
        relatedEquipment: relatedEquipment.length > 0 ? relatedEquipment : undefined,
      };

      let result;
      if (isEdit && role) {
        const input: UpdateBusinessRoleInput = { ...base, id: role.id, revisionComment: revisionComment || undefined };
        result = await updateBusinessRole(input);
      } else {
        result = await createBusinessRole(base);
      }

      if (result.errors.length === 0) {
        onSaved();
        onClose();
      } else {
        setError(result.errors[0]?.description || t('businessRoles.errors.saveFailed'));
      }
    } catch {
      setError(t('businessRoles.errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleAutoFill = async () => {
    if (!name.trim() || aiFilling) return;
    setAiFilling(true);
    setAiNotice('');
    try {
      const result = await suggestRole({ name: name.trim(), category: category || undefined, description: description || undefined });
      if (result.errors.length > 0) {
        setAiNotice(result.errors[0]?.description || t('businessRoles.aiFailed'));
        return;
      }
      const s = result.payload;
      if (!s) return;
      if (s.aiAvailable === false) {
        setAiNotice(t('businessRoles.aiNotConfigured'));
        return;
      }
      if (s.description) setDescription(s.description);
      if (s.category) setCategory(s.category);
      if (s.coreResponsibilities?.length) setCoreResponsibilities(s.coreResponsibilities);
      if (s.decisionRights?.length) setDecisionRights(s.decisionRights);
      if (s.dependencies?.length) setDependencies(s.dependencies.map((d) => ({ role: d.role, description: d.description })));
      if (s.requiredSkills?.length) setRequiredSkills(s.requiredSkills);
      if (s.neededEquipment?.length) setNeededEquipment(s.neededEquipment);
      if (s.relatedEquipment?.length) {
        // A category must never be both needed and related. The AI prompt forbids the overlap,
        // but LLMs can slip — drop any related id the model also returned as needed.
        const neededIds = new Set((s.neededEquipment ?? []).map((e) => e.categoryId));
        setRelatedEquipment(s.relatedEquipment.filter((id) => !neededIds.has(id)));
      }
      if (s.successCriteria) setSuccessCriteria((prev) => ({ ...prev, description: s.successCriteria }));
      if (s.tags?.length) setTags(s.tags);

      const filled: AiField[] = [];
      if (s.description) filled.push('description');
      if (s.category) filled.push('category');
      if (s.coreResponsibilities?.length) filled.push('coreResponsibilities');
      if (s.decisionRights?.length) filled.push('decisionRights');
      if (s.dependencies?.length) filled.push('dependencies');
      if (s.requiredSkills?.length) filled.push('requiredSkills');
      if (s.neededEquipment?.length) filled.push('neededEquipment');
      if (s.relatedEquipment?.length) filled.push('relatedEquipment');
      if (s.successCriteria) filled.push('successCriteria');
      if (s.tags?.length) filled.push('tags');
      markAiFilled(filled);

      // Structured skills, so a new role is born matched to the catalogue and never has to be
      // reconciled later. The free-text list above stays for anything the catalogue can't match.
      const skillResult = await suggestRequiredSkills({
        name: name.trim(),
        category: s.category || category || undefined,
        description: s.description || description || undefined,
        coreResponsibilities: s.coreResponsibilities?.length ? s.coreResponsibilities : (coreResponsibilities.length > 0 ? coreResponsibilities : undefined),
      });
      if (skillResult.errors.length === 0 && skillResult.payload?.skills?.length) {
        const picked = skillResult.payload.skills;
        setSkillCatalogue((prev) => {
          const next = [...prev];
          picked.forEach((p) => { if (!next.some((c) => c.id === p.skillId)) next.push({ id: p.skillId, name: p.skillName }); });
          return next;
        });
        setRequiredSkillItems(picked.map((p) => ({ skillId: p.skillId, importance: p.importance })));
        markAiFilled(['requiredSkillItems']);
      }

      setShowDisclaimer(true);
    } catch {
      setAiNotice(t('businessRoles.aiFailed'));
    } finally {
      setAiFilling(false);
    }
  };

  const handleSuggestSkillMappings = async () => {
    if (requiredSkills.length === 0 || skillMapping) return;
    setSkillMapping(true);
    setAiNotice('');
    try {
      const result = await suggestSkillMappings(requiredSkills);
      if (result.errors.length > 0) {
        setAiNotice(result.errors[0]?.description || t('businessRoles.aiFailed'));
        return;
      }
      const payload = result.payload;
      if (!payload) return;
      if (payload.aiAvailable === false) {
        setAiNotice(t('businessRoles.aiNotConfigured'));
        return;
      }
      setSkillMappings(payload.mappings ?? []);
    } catch {
      setAiNotice(t('businessRoles.aiFailed'));
    } finally {
      setSkillMapping(false);
    }
  };

  // Staged only — the match is written to requiredSkillItems in local state and the score
  // moves when the role is saved, never on Accept alone.
  const acceptSkillMapping = (mapping: SkillMappingSuggestionItemView) => {
    if (mapping.suggestedSkillId == null) return;
    addRequiredSkill(mapping.suggestedSkillId, mapping.suggestedSkillName ?? String(mapping.suggestedSkillId));
    setRequiredSkills((prev) => prev.filter((n) => n !== mapping.rawName));
    setSkillMappings((prev) => prev.filter((m) => m.rawName !== mapping.rawName));
  };

  const handleGenerateCriteria = async () => {
    if (!name.trim() || aiCriteria) return;
    setAiCriteria(true);
    setAiNotice('');
    try {
      const result = await suggestSuccessCriteria({
        name: name.trim(),
        coreResponsibilities: coreResponsibilities.length > 0 ? coreResponsibilities : undefined,
        decisionRights: decisionRights.length > 0 ? decisionRights : undefined,
      });
      if (result.errors.length > 0) {
        setAiNotice(result.errors[0]?.description || t('businessRoles.aiFailed'));
        return;
      }
      if (result.payload) setSuccessCriteria((prev) => ({ ...prev, description: result.payload }));
    } catch {
      setAiNotice(t('businessRoles.aiFailed'));
    } finally {
      setAiCriteria(false);
    }
  };


  // Adds the skill to the company catalogue unassigned; the caller then attaches it to this role.
  const handleCreateSkill = async (skillName: string): Promise<SkillView | null> => {
    const result = await createSkill({ name: skillName });
    if (result.errors.length > 0 || !result.payload) return null;
    setSkillCatalogue((prev) => prev.some((s) => s.id === result.payload!.id) ? prev : [...prev, result.payload!]);
    return result.payload;
  };

  // Same inline-backfill interaction as departments and skills: a category the company has not set up
  // yet can be added from here rather than sending the user to Settings and losing the half-filled form.
  const handleCreateCategory = async (categoryName: string): Promise<number | null> => {
    const result = await addRoleCategory({ name: categoryName, isActive: true });
    if (result.errors.length > 0 || !result.payload) return null;
    setRoleCategories((prev) => [...prev, result.payload!]);
    return result.payload.id;
  };

  // Batch upsert: sending one row with id 0 inserts it and leaves every other department untouched.
  const handleCreateDepartment = async (departmentName: string): Promise<number | null> => {
    const result = await addUpdateDepartments([{ id: 0, name: departmentName }]);
    if (result.errors.length > 0 || !result.payload) return null;
    setDepartments(result.payload);
    return result.payload.find((d) => d.name.toLowerCase() === departmentName.toLowerCase())?.id ?? null;
  };


  // The error renders at the top of a long form while submit sits in the footer, so it lands off-screen.
  useEffect(() => {
    if (error) errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [error]);

  const addListItem = (list: string[], setList: (v: string[]) => void, value: string, setValue: (v: string) => void, field?: AiField) => {
    if (value.trim() && !list.includes(value.trim())) {
      if (field) clearAiFilled(field);
      setList([...list, value.trim()]);
      setValue('');
    }
  };

  const removeListItem = (list: string[], setList: (v: string[]) => void, index: number, field?: AiField) => {
    if (field) clearAiFilled(field);
    setList(list.filter((_, i) => i !== index));
  };

  const addDependency = () => {
    setDependencies([...dependencies, { role: '', description: '' }]);
  };

  const updateDependency = (index: number, field: keyof BusinessRoleDependencyInput, value: string) => {
    clearAiFilled('dependencies');
    setDependencies(dependencies.map((d, i) => i === index ? { ...d, [field]: value } : d));
  };

  const removeDependency = (index: number) => {
    clearAiFilled('dependencies');
    setDependencies(dependencies.filter((_, i) => i !== index));
  };

  // Structured skills are catalogue references, so an id is required — a typed name that
  // matched nothing is left in the legacy list rather than saved as an unscorable item.
  const addRequiredSkill = (skillId: number, skillName: string) => {
    clearAiFilled('requiredSkillItems');
    if (!skillId || requiredSkillItems.some((s) => s.skillId === skillId)) return;
    setSkillCatalogue((prev) => (prev.some((c) => c.id === skillId) ? prev : [...prev, { id: skillId, name: skillName }]));
    setRequiredSkillItems((prev) => [...prev, { skillId, importance: EquipmentPriority.Important }]);
  };

  const updateRequiredSkillImportance = (skillId: number, importance: EquipmentPriority) => {
    setRequiredSkillItems(requiredSkillItems.map((s) => (s.skillId === skillId ? { ...s, importance } : s)));
  };

  const removeRequiredSkill = (skillId: number) => {
    clearAiFilled('requiredSkillItems');
    setRequiredSkillItems(requiredSkillItems.filter((s) => s.skillId !== skillId));
  };

  const addEquipment = (categoryId: number) => {
    clearAiFilled('neededEquipment');
    if (!categoryId || neededEquipment.some((e) => e.categoryId === categoryId)) return;
    setNeededEquipment([
      ...neededEquipment,
      { categoryId, categoryName: categoryNameById.get(categoryId), priority: EquipmentPriority.Important, quantity: 1 },
    ]);
    // A category can't be both required and a related extra — promoting it to needed drops the chip.
    setRelatedEquipment((prev) => prev.filter((id) => id !== categoryId));
  };

  const updateEquipmentPriority = (categoryId: number, priority: EquipmentPriority) => {
    clearAiFilled('neededEquipment');
    setNeededEquipment(neededEquipment.map((e) => e.categoryId === categoryId ? { ...e, priority } : e));
  };

  const updateEquipmentQuantity = (categoryId: number, quantity: number) => {
    clearAiFilled('neededEquipment');
    const q = Number.isFinite(quantity) && quantity >= 1 ? Math.floor(quantity) : 1;
    setNeededEquipment(neededEquipment.map((e) => e.categoryId === categoryId ? { ...e, quantity: q } : e));
  };

  const addRelatedEquipment = (categoryId: number) => {
    clearAiFilled('relatedEquipment');
    if (!categoryId || relatedEquipment.includes(categoryId) || neededEquipment.some((e) => e.categoryId === categoryId)) return;
    setRelatedEquipment([...relatedEquipment, categoryId]);
  };

  const removeRelatedEquipment = (categoryId: number) => {
    clearAiFilled('relatedEquipment');
    setRelatedEquipment(relatedEquipment.filter((id) => id !== categoryId));
  };

  const removeEquipment = (categoryId: number) => {
    clearAiFilled('neededEquipment');
    setNeededEquipment(neededEquipment.filter((e) => e.categoryId !== categoryId));
  };

  // A free-text category inherited from an older role is not in the configured list; it gets a
  // sentinel id so the picker can still show and keep it.
  const LEGACY_CATEGORY_ID = -1;
  const categoryOptions = [
    ...(category && !roleCategories.some((c) => c.name === category)
      ? [{ value: LEGACY_CATEGORY_ID, label: category }]
      : []),
    ...roleCategories.map((c) => ({ value: c.id, label: c.name })),
  ];

  const inputClass = 'w-full border border-border rounded-lg px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary';

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen && !saving) onClose(); }}>
      <DialogContent size="lg" className="max-w-3xl">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-primary-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <DialogTitle>
              {isEdit ? t('businessRoles.editRole') : t('businessRoles.createRole')}
            </DialogTitle>
          </div>
        </DialogHeader>

        <DialogBody className="space-y-6">
          {error && (
            <div ref={errorRef} data-testid="role-form-error" className="bg-destructive/5 border border-destructive/20 text-destructive rounded-lg px-4 py-3 text-sm">{error}</div>
          )}

          {/* Auto-fill with AI — create only. Editing an existing role never regenerates it. */}
          {!isEdit && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-muted/40 px-4 py-3">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-foreground">{t('businessRoles.aiAutoFillTitle')}</p>
              <p className="text-xs text-muted-foreground">{t('businessRoles.aiAutoFillHint')}</p>
            </div>
            <Button
              onClick={handleAutoFill}
              disabled={!name.trim() || aiFilling}
              loading={aiFilling}
              iconName="Sparkles"
              data-testid="ai-autofill-button"
            >
              {t('businessRoles.aiAutoFill')}
            </Button>
          </div>
          )}

          {showDisclaimer && (
            <div className="flex items-start gap-2 rounded-lg border border-primary/20 bg-primary/5 px-4 py-3 text-sm text-primary" data-testid="ai-disclaimer">
              <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              <span>{t('businessRoles.aiDisclaimer')}</span>
            </div>
          )}

          {aiNotice && (
            <div className="flex items-start justify-between gap-2 rounded-lg border border-warning/20 bg-warning/5 px-4 py-3 text-sm text-warning">
              <span>{aiNotice}</span>
              <button type="button" onClick={() => setAiNotice('')} className="text-warning hover:opacity-70" aria-label={t('businessRoles.dismiss')}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
          )}

          {/* Role Information */}
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-4">{t('businessRoles.roleInformation')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">{t('businessRoles.roleName')} *</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className={inputClass}
                  data-testid="role-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">{t('businessRoles.roleType')}</label>
                <select
                  value={type}
                  onChange={(e) => handleTypeChange(e.target.value as BusinessRoleType)}
                  className={inputClass}
                  data-testid="role-type-select"
                >
                  {Object.entries(roleTypeLabels).map(([val, label]) => (
                    <option key={val} value={val}>{t(`businessRoles.type${label}`)}</option>
                  ))}
                </select>
              </div>
              {/* A scoped manager sees the department on every type, because for them a Project role carries it too. */}
              {(type === BusinessRoleType.Department || departmentLocked) && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    {t('businessRoles.department')}
                    {type === BusinessRoleType.Department && !departmentLocked && <span> *</span>}
                  </label>
                  <CreatableSelect
                    value={departmentId}
                    onChange={setDepartmentId}
                    // Omitted for Manager and Chief: AddUpdateDepartments would reject them, so never offer it.
                    onCreate={canCreateDepartment && !departmentLocked ? handleCreateDepartment : undefined}
                    options={[
                      // Keeps the locked field readable when the scope department is no longer listed.
                      ...(departmentLocked && departmentId === scopeDepartmentId && lockedDepartmentName
                        && !departments.some((d) => d.id === departmentId)
                        ? [{ value: departmentId, label: lockedDepartmentName }]
                        : []),
                      ...departments.map((d) => ({ value: d.id, label: d.name })),
                    ]}
                    placeholder={t('businessRoles.selectDepartment')}
                    disabled={departmentLocked}
                    data-testid="role-department-select"
                  />
                  {departmentLocked && (
                    <p id="role-department-locked-note" className="mt-1 text-xs text-muted-foreground" data-testid="role-department-locked-note">
                      {lockedDepartmentName
                        ? t('businessRoles.departmentLockedToScope', { department: lockedDepartmentName })
                        : t('businessRoles.departmentLockedToScopeUnknown')}
                    </p>
                  )}
                </div>
              )}
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <label className="block text-sm font-medium text-foreground">{t('businessRoles.category')}</label>
                  <AiGeneratedBadge visible={isAiFilled('category')} />
                </div>
                {/* `category` is stored as a NAME, not an id, so the id is only a handle for the
                    picker. LEGACY_CATEGORY_ID keeps a free-text category from an older role
                    selectable instead of silently clearing it when it is not in the configured list. */}
                <CreatableSelect
                  value={categoryOptions.find((o) => o.label === category)?.value}
                  onChange={(value) => {
                    clearAiFilled('category');
                    setCategory(value == null ? '' : (categoryOptions.find((o) => o.value === value)?.label ?? ''));
                  }}
                  // Hidden for anyone AddCategory would reject rather than shown and left to fail.
                  onCreate={canCreateLookups ? handleCreateCategory : undefined}
                  options={categoryOptions}
                  placeholder={t('businessRoles.selectCategory')}
                  data-testid="role-category-select"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">{t('businessRoles.status')}</label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as BusinessRoleStatus)}
                  className={inputClass}
                  data-testid="role-status-select"
                >
                  {Object.entries(statusLabels).map(([val, label]) => (
                    <option key={val} value={val}>{t(`businessRoles.status${label}`)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">{t('businessRoles.numberOfSeats')}</label>
                <input
                  type="number"
                  min={1}
                  value={numberOfSeats}
                  onChange={(e) => setNumberOfSeats(Math.max(1, parseInt(e.target.value) || 1))}
                  className={inputClass}
                  data-testid="role-seats-input"
                />
              </div>
            </div>
            <div className="mt-4">
              <div className="flex items-center gap-2 mb-1">
                <label className="block text-sm font-medium text-foreground">{t('businessRoles.description')}</label>
                <AiGeneratedBadge visible={isAiFilled('description')} />
              </div>
              <textarea
                value={description}
                onChange={(e) => { clearAiFilled('description'); setDescription(e.target.value); }}
                rows={3}
                className={inputClass}
                data-testid="role-description-input"
              />
            </div>
          </div>

          {/* Tags */}
          <div>
            <div className="flex items-center gap-2 mb-1">
              <label className="block text-sm font-semibold text-foreground">{t('businessRoles.tags')}</label>
              <AiGeneratedBadge visible={isAiFilled('tags')} />
            </div>
            <p className="text-xs text-muted-foreground mb-2">{t('businessRoles.tagsHint')}</p>
            <div className="flex flex-wrap gap-2 mb-2">
              {tags.map((tag, i) => (
                <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded-md text-sm">
                  {tag}
                  <button onClick={() => removeListItem(tags, setTags, i, 'tags')} className="text-primary hover:text-primary">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addListItem(tags, setTags, newTag, setNewTag, 'tags'))}
                placeholder={t('businessRoles.addTag')}
                className={`flex-1 ${inputClass}`}
              />
              <Button variant="secondary" size="sm" onClick={() => addListItem(tags, setTags, newTag, setNewTag, 'tags')}>
                {t('businessRoles.add')}
              </Button>
            </div>
          </div>

          {/* Required Skills — catalogue picks carry an importance and score; typed names below don't. */}
          <div data-testid="required-skills-picker">
            <div className="flex items-center gap-2 mb-2">
              <label className="block text-sm font-semibold text-foreground">{t('businessRoles.requiredSkills')}</label>
              <AiGeneratedBadge visible={isAiFilled('requiredSkills') || isAiFilled('requiredSkillItems')} />
            </div>
            <div className="space-y-2 mb-2">
              {requiredSkillItems.map((item) => (
                <div
                  key={item.skillId}
                  className="flex items-center gap-3 rounded-lg border border-border bg-card px-3 py-2"
                  data-testid={`required-skill-${item.skillId}`}
                >
                  <span className="flex-1 text-sm text-foreground">
                    {skillNameById.get(item.skillId) ?? String(item.skillId)}
                  </span>
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-muted-foreground">{t('businessRoles.priority.label')}</span>
                    <select
                      value={item.importance}
                      onChange={(e) => updateRequiredSkillImportance(item.skillId, e.target.value as EquipmentPriority)}
                      className={`rounded-lg border px-2 py-1 text-xs font-medium focus-visible:ring-2 focus-visible:ring-primary/20 ${priorityToneClass[item.importance]}`}
                      data-testid={`required-skill-importance-${item.skillId}`}
                    >
                      {priorityOrder.map((p) => (
                        <option key={p} value={p}>{t(`businessRoles.priority.${p.toLowerCase()}`)}</option>
                      ))}
                    </select>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeRequiredSkill(item.skillId)}
                    className="text-muted-foreground hover:text-destructive"
                    aria-label={t('businessRoles.remove')}
                    data-testid={`required-skill-remove-${item.skillId}`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>
              ))}
            </div>
            <SkillAutocomplete
              value={skillQuery}
              onCreate={handleCreateSkill}
              placeholder={t('businessRoles.addSkill')}
              onChange={(value, skillId) => {
                if (skillId) {
                  addRequiredSkill(skillId, value);
                  setSkillQuery('');
                } else {
                  setSkillQuery(value);
                }
              }}
            />

            {/* Legacy typed skills — read-only until matched to a catalogue skill. */}
            {requiredSkills.length > 0 && (
              <div className="mt-4 rounded-lg border border-border bg-muted/40 px-3 py-3" data-testid="legacy-skills-section">
                <p className="text-xs text-muted-foreground mb-2">{t('businessRoles.legacySkillsHint')}</p>
                <div className="flex flex-wrap gap-2">
                  {requiredSkills.map((skill, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center rounded-md border border-border bg-card px-2 py-1 text-sm text-muted-foreground"
                      data-testid={`legacy-skill-${skill}`}
                    >
                      {skill}
                    </span>
                  ))}
                </div>
                <div className="mt-3">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleSuggestSkillMappings}
                    disabled={skillMapping}
                    loading={skillMapping}
                    iconName="Sparkles"
                    data-testid="skill-mapping-suggest-btn"
                  >
                    {t('businessRoles.suggestSkillMatches', 'Find catalogue matches')}
                  </Button>
                </div>

                {skillMappings.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {skillMappings.map((m) => (
                      <div
                        key={m.rawName}
                        className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-card px-3 py-2"
                        data-testid={`skill-mapping-${m.rawName}`}
                      >
                        <span className="text-sm text-foreground">{m.rawName}</span>
                        {m.suggestedSkillId != null ? (
                          <>
                            <span className="text-xs text-muted-foreground">{'→'}</span>
                            <span className="flex-1 text-sm font-medium text-foreground">{m.suggestedSkillName}</span>
                            {m.reason && <span className="w-full text-xs text-muted-foreground">{m.reason}</span>}
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => acceptSkillMapping(m)}
                              data-testid={`skill-mapping-accept-${m.rawName}`}
                            >
                              {t('businessRoles.acceptSkillMatch', 'Accept')}
                            </Button>
                          </>
                        ) : (
                          <span className="flex-1 text-sm text-muted-foreground">
                            {t('businessRoles.noSkillMatch', 'No match found')}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Needed Equipment */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <label className="block text-sm font-semibold text-foreground">{t('businessRoles.neededEquipment')}</label>
              <AiGeneratedBadge visible={isAiFilled('neededEquipment')} />
            </div>
            <div className="space-y-2 mb-2">
              {neededEquipment.map((item) => (
                <div key={item.categoryId} className="flex items-center gap-3 rounded-lg border border-border bg-card px-3 py-2">
                  <span className="flex-1 text-sm text-foreground">
                    {item.categoryName ?? categoryNameById.get(item.categoryId) ?? String(item.categoryId)}
                  </span>
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-muted-foreground">{t('businessRoles.quantity')}</span>
                    <input
                      type="number"
                      min={1}
                      value={item.quantity ?? 1}
                      onChange={(e) => updateEquipmentQuantity(item.categoryId, parseInt(e.target.value, 10))}
                      className="w-16 rounded-lg border border-border bg-card px-2 py-1 text-xs font-medium focus-visible:ring-2 focus-visible:ring-primary/20"
                      data-testid={`equipment-quantity-${item.categoryId}`}
                    />
                    <span className="text-xs text-muted-foreground ms-1">{t('businessRoles.priority.label')}</span>
                    <select
                      value={item.priority}
                      onChange={(e) => updateEquipmentPriority(item.categoryId, e.target.value as EquipmentPriority)}
                      className={`rounded-lg border px-2 py-1 text-xs font-medium focus-visible:ring-2 focus-visible:ring-primary/20 ${priorityToneClass[item.priority]}`}
                      data-testid={`equipment-priority-${item.categoryId}`}
                    >
                      {priorityOrder.map((p) => (
                        <option key={p} value={p}>{t(`businessRoles.priority.${p.toLowerCase()}`)}</option>
                      ))}
                    </select>
                  </div>
                  <button type="button" onClick={() => removeEquipment(item.categoryId)} className="text-muted-foreground hover:text-destructive" aria-label={t('businessRoles.remove')}>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>
              ))}
            </div>
            {/* A category that does not exist yet is created here, in the group the user picks,
                rather than sending them to asset settings with a half-written role behind them. */}
            <EquipmentCategoryPicker
              categories={categories}
              excludeIds={neededEquipment.map((e) => e.categoryId)}
              onPick={(categoryId) => addEquipment(categoryId)}
              onCreated={(created) => setCategories((prev) => [...prev, created])}
              data-testid="equipment-add-select"
            />
          </div>

          {/* Related equipment (optional) — relevant-but-not-required; grants a small coverage bonus */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <label className="block text-sm font-semibold text-foreground">{t('businessRoles.relatedEquipment')}</label>
              <AiGeneratedBadge visible={isAiFilled('relatedEquipment')} />
            </div>
            <p className="text-xs text-muted-foreground mb-2">{t('businessRoles.relatedEquipmentHint')}</p>
            {relatedEquipment.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {relatedEquipment.map((id) => (
                  <span
                    key={id}
                    className="inline-flex items-center gap-1 rounded-full border border-border bg-muted px-3 py-1 text-xs text-foreground"
                    data-testid={`related-equipment-chip-${id}`}
                  >
                    {categoryNameById.get(id) ?? String(id)}
                    <button
                      type="button"
                      onClick={() => removeRelatedEquipment(id)}
                      className="text-muted-foreground hover:text-destructive"
                      aria-label={t('businessRoles.remove')}
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </span>
                ))}
              </div>
            )}
            {level2Categories.length > 0 && (
              <select
                value=""
                onChange={(e) => addRelatedEquipment(parseInt(e.target.value, 10))}
                className={inputClass}
                data-testid="related-equipment-add-select"
              >
                <option value="">{t('businessRoles.relatedEquipmentPick')}</option>
                {level2Categories
                  .filter((c) => !relatedEquipment.includes(c.id) && !neededEquipment.some((e) => e.categoryId === c.id))
                  .map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
              </select>
            )}
          </div>

          {/* Core Responsibilities */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <label className="block text-sm font-semibold text-foreground">{t('businessRoles.coreResponsibilities')}</label>
              <AiGeneratedBadge visible={isAiFilled('coreResponsibilities')} />
            </div>
            <div className="space-y-2 mb-2">
              {coreResponsibilities.map((resp, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="text-muted-foreground text-sm">{'•'}</span>
                  <span className="flex-1 text-sm text-foreground">{resp}</span>
                  <button onClick={() => removeListItem(coreResponsibilities, setCoreResponsibilities, i, 'coreResponsibilities')} className="text-muted-foreground hover:text-destructive">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                  </button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={newResponsibility}
                onChange={(e) => setNewResponsibility(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addListItem(coreResponsibilities, setCoreResponsibilities, newResponsibility, setNewResponsibility, 'coreResponsibilities'))}
                placeholder={t('businessRoles.addResponsibility')}
                className={`flex-1 ${inputClass}`}
              />
              <Button variant="secondary" size="sm" onClick={() => addListItem(coreResponsibilities, setCoreResponsibilities, newResponsibility, setNewResponsibility, 'coreResponsibilities')}>
                {t('businessRoles.add')}
              </Button>
            </div>
          </div>

          {/* Decision Rights */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <label className="block text-sm font-semibold text-foreground">{t('businessRoles.decisionRights')}</label>
              <AiGeneratedBadge visible={isAiFilled('decisionRights')} />
            </div>
            <div className="space-y-2 mb-2">
              {decisionRights.map((dr, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="text-muted-foreground text-sm">{'•'}</span>
                  <span className="flex-1 text-sm text-foreground">{dr}</span>
                  <button onClick={() => removeListItem(decisionRights, setDecisionRights, i, 'decisionRights')} className="text-muted-foreground hover:text-destructive">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                  </button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={newDecisionRight}
                onChange={(e) => setNewDecisionRight(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addListItem(decisionRights, setDecisionRights, newDecisionRight, setNewDecisionRight, 'decisionRights'))}
                placeholder={t('businessRoles.addDecisionRight')}
                className={`flex-1 ${inputClass}`}
              />
              <Button variant="secondary" size="sm" onClick={() => addListItem(decisionRights, setDecisionRights, newDecisionRight, setNewDecisionRight, 'decisionRights')}>
                {t('businessRoles.add')}
              </Button>
            </div>
          </div>

          {/* Dependencies */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <label className="block text-sm font-semibold text-foreground">{t('businessRoles.dependencies')}</label>
              <AiGeneratedBadge visible={isAiFilled('dependencies')} />
            </div>
            <div className="space-y-3 mb-2">
              {dependencies.map((dep, i) => (
                <div key={i} className="grid grid-cols-1 md:grid-cols-2 gap-2 p-3 bg-muted rounded-lg">
                  <select
                    value={dep.role}
                    onChange={(e) => updateDependency(i, 'role', e.target.value)}
                    className="border border-border rounded-lg px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary bg-card"
                  >
                    <option value="">{t('businessRoles.dependencyRole')}</option>
                    {dep.role && !roleNames.includes(dep.role) && (
                      <option value={dep.role}>{dep.role}</option>
                    )}
                    {roleNames.map((rn) => (
                      <option key={rn} value={rn}>{rn}</option>
                    ))}
                  </select>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={dep.description}
                      onChange={(e) => updateDependency(i, 'description', e.target.value)}
                      placeholder={t('businessRoles.dependencyDescription')}
                      className="flex-1 border border-border rounded-lg px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                    />
                    <button onClick={() => removeDependency(i)} className="text-muted-foreground hover:text-destructive">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <Button variant="link" size="sm" onClick={addDependency} iconName="Plus">
              {t('businessRoles.addDependency')}
            </Button>
          </div>

          {/* Success Criteria */}
          <div className="bg-warning/5 border border-warning/20 rounded-lg p-4">
            <div className="flex items-center justify-between gap-3 mb-3">
              <label className="block text-sm font-semibold text-foreground">{t('businessRoles.successCriteria')}</label>
              <AiGeneratedBadge visible={isAiFilled('successCriteria')} />
              {/* AI generation is create-only. */}
              {!isEdit && (
              <Button
                variant="secondary"
                size="sm"
                onClick={handleGenerateCriteria}
                disabled={!name.trim() || aiCriteria}
                loading={aiCriteria}
                iconName="Sparkles"
                data-testid="ai-criteria-button"
              >
                {t('businessRoles.generateWithAi')}
              </Button>
              )}
            </div>
            <textarea
              value={successCriteria.description || ''}
              onChange={(e) => { clearAiFilled('successCriteria'); setSuccessCriteria({ ...successCriteria, description: e.target.value }); }}
              rows={3}
              placeholder={t('businessRoles.successCriteriaPlaceholder')}
              className={`${inputClass} bg-card`}
              data-testid="success-criteria-input"
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
              <div>
                <label className="block text-xs text-muted-foreground mb-1">{t('businessRoles.lastReviewDate')}</label>
                <input
                  type="date"
                  value={todayStr}
                  disabled
                  className={`${inputClass} bg-muted text-muted-foreground`}
                  data-testid="last-review-date"
                />
              </div>
              <div>
                <label className="block text-xs text-muted-foreground mb-1">{t('businessRoles.nextReviewDate')}</label>
                <input
                  type="date"
                  value={nextReviewStr}
                  disabled
                  className={`${inputClass} bg-muted text-muted-foreground`}
                  data-testid="next-review-date"
                />
              </div>
              <div>
                <label className="block text-xs text-muted-foreground mb-1">{t('businessRoles.reviewedBy')}</label>
                <input
                  type="text"
                  value={currentUserName || ''}
                  disabled
                  className={`${inputClass} bg-muted text-muted-foreground`}
                  data-testid="reviewed-by"
                />
              </div>
            </div>
          </div>

          {/* Revision Comment (edit only) */}
          {isEdit && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">{t('businessRoles.revisionComment')}</label>
              <input
                type="text"
                value={revisionComment}
                onChange={(e) => setRevisionComment(e.target.value)}
                placeholder={t('businessRoles.revisionCommentPlaceholder')}
                className={inputClass}
              />
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          <ClaritySavingHint visible={saving} className="me-auto" />
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {t('businessRoles.cancel')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            loading={saving}
            data-testid="save-role-button"
          >
            {isEdit ? t('businessRoles.saveChanges') : t('businessRoles.createRole')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
